"use client";
import { usePreviewDialogStore } from "@/components/Search/PreviewDialogProvider";
import useLibraryBlockedDialog from "@/hooks/useLibraryBlockedDialog";
import { useRouter } from "next/router";
import { shouldSkipNPdfDialog } from "@/components/Library/utils/nPdfLocalStorage";
import { mixpanelEvent, popupButtonClicked, popupViewed } from "@/lib/mixpanels/mixpanel";
import { useCallback, useState } from "react";
import { requestProductUse } from "@/lib/entitlement/entitlementRequest";
import { getDownloadAvailability } from "../../utils/downloadUtils";
import { checkEntitlementFirstUse, getUsableEntitlement } from "../../utils/entitlement";
import { PASSES_LANDING, VIEWER_PAGE } from "src/settings/constant";
import { useDialogStore } from "src/store/useDialogStore";
import useSnackbar from "src/store/useSnackbar";
import { isInWebview } from "src/utils/misc";
import { useFreeTrialDialog } from "./FreeTrialDialog";
import { useLicenseUseDialog } from "./LicenseUseDialog";
import useLibraryFirstUseCheck from "./useLibraryFirstUseCheck";
import { getProductType } from "./ProductListItem";
/**
 * 팝업 또는 새 탭 열기 유틸리티
 * - 웹뷰 환경: document 객체 반환 (앱에서 처리)
 * - 브라우저 환경: 새 탭 열기
 */ const getPopup = ()=>isInWebview() ? document : window.open("about:blank", "_blank");
/**
 * 뷰어 페이지 열기 유틸리티
 * - 새 탭/팝업에서 뷰어 페이지를 열어줌
 * - 에디션/카테고리 페이지에서는 licenseId를 사용
 */ const openViewerPage = (licenseId, popup)=>{
    if (!popup) {
        popup = getPopup();
    }
    if (!popup) {
        ;
        return;
    }
    popup.location.href = "".concat(VIEWER_PAGE, "/").concat(licenseId);
};
/**
 * author_id에 따른 이용권 페이지 경로 반환
 * - 스테이징/로컬: 76777 → teacher, 77063 → student
 * - 프로덕션: 76777 → teacher?subject=EN, 46282 → teacher?subject=KO, 102546 → student
 */ export const getPassesUrlByAuthorId = (authorId)=>{
    const isProduction = "production" === "production";
    const numericAuthorId = typeof authorId === "string" ? parseInt(authorId, 10) : authorId;
    if (isProduction) {
        switch(numericAuthorId){
            case 76777:
                return "".concat(PASSES_LANDING, "/teacher?subject=EN") // 쏠북패스 영어
                ;
            case 46282:
                return "".concat(PASSES_LANDING, "/teacher?subject=KO") // 쏠북패스 국어
                ;
            case 102546:
                return "".concat(PASSES_LANDING, "/student") // 쏠북패스 전과목
                ;
            default:
                return "".concat(PASSES_LANDING, "/teacher");
        }
    } else {
        switch(numericAuthorId){
            case 76777:
                return "".concat(PASSES_LANDING, "/teacher?subject=EN");
            case 46282:
                return "".concat(PASSES_LANDING, "/teacher?subject=KO");
            case 77063:
                return "".concat(PASSES_LANDING, "/student");
            default:
                return "".concat(PASSES_LANDING, "/teacher");
        }
    }
};
/**
 * 상품 아이템 공통 핸들러 훅
 * - ProductList, AccordionProductList에서 공통으로 사용
 * - 샘플 보기, CTA, PDF 다운로드 핸들러 제공
 * - 다운로드 다이얼로그 상태 관리
 */ export const useProductItemHandlers = (options)=>{
    var _acquiredLicenses_nPdfTargetItem_id;
    const router = useRouter();
    const { openFreeTrialDialog  } = useFreeTrialDialog();
    const { openLicenseUseDialog  } = useLicenseUseDialog();
    const openBlockedDialog = useLibraryBlockedDialog();
    const { openPreviewDialog  } = usePreviewDialogStore();
    const { addSnackbar  } = useSnackbar();
    const { open: openBrowserDialog , close: closeBrowserDialog  } = useDialogStore();
    const { checkFirstUse , logUsage  } = useLibraryFirstUseCheck();
    const { purchaseUrl , pageType  } = options !== null && options !== void 0 ? options : {};
    const position = pageType === "system" ? "시스템 카테고리" : "에디션 카테고리";
    /**
   * 카카오/네이버 인앱 브라우저 여부 체크
   * - 인앱 브라우저면 다운로드 불가 다이얼로그 표시 후 false 반환
   * - 일반 브라우저면 true 반환
   */ const checkDownloadBrowser = useCallback(()=>{
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("naver") || userAgent.includes("kakao")) {
            const dialogId = "download-unavailable-browser";
            // mx-414: Popup Viewed
            popupViewed({
                Position: position,
                Type: "다운로드 불가 안내(이유:모바일>네이버/카카오인앱)",
                Title: "PDF 다운로드가 지원되지 않는 환경이에요.",
                Body: "크롬, 삼성 인터넷, 사파리 등 다른 브라우저에서 다시 시도해주세요."
            });
            openBrowserDialog({
                id: dialogId,
                type: "confirm",
                props: {
                    title: "PDF 다운로드가 지원되지 않는 환경이에요.",
                    confirmText: "확인",
                    description: "크롬, 삼성 인터넷, 사파리 등 다른 브라우저에서 다시 시도해주세요.",
                    severity: "error",
                    onConfirm: ()=>{
                        // mx-415: Popup Button Clicked
                        popupButtonClicked({
                            Position: position,
                            Type: "다운로드 불가 안내(이유:모바일>네이버/카카오인앱)",
                            Title: "PDF 다운로드가 지원되지 않는 환경이에요.",
                            Body: "크롬, 삼성 인터넷, 사파리 등 다른 브라우저에서 다시 시도해주세요.",
                            "Button Name": "확인",
                            "Button Url": null
                        });
                        closeBrowserDialog(dialogId);
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            closeBrowserDialog(dialogId);
                        }
                    }
                }
            });
            return false;
        }
        return true;
    }, [
        openBrowserDialog,
        closeBrowserDialog,
        position
    ]);
    /**
   * handoutType: "basic" 자료의 열람 환불 확인 다이얼로그
   */ const openRefundConfirmDialog = useCallback((onConfirm)=>{
        const dialogId = "basic-refund-confirm";
        openBrowserDialog({
            id: dialogId,
            type: "confirm",
            props: {
                title: "자료를 열어보시겠어요?",
                description: "열람+출력 후에는 자료를 환불 및 교환할 수 없어요.",
                confirmText: "열기",
                cancelText: "취소",
                onConfirm: ()=>{
                    closeBrowserDialog(dialogId);
                    onConfirm();
                },
                contentProps: {
                    onInteractOutside: ()=>closeBrowserDialog(dialogId)
                }
            }
        });
    }, [
        openBrowserDialog,
        closeBrowserDialog
    ]);
    // PDF 다운로드 다이얼로그 상태
    const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
    const [downloadData, setDownloadData] = useState(null);
    const [isFirstUseForDownload, setIsFirstUseForDownload] = useState(false);
    // N-PDF 다이얼로그 상태 (nPdfTargetItem이 있으면 다이얼로그 열림)
    const [nPdfTargetItem, setNPdfTargetItem] = useState(null);
    const [nPdfActionType, setNPdfActionType] = useState("download");
    // 낙관적 업데이트: 획득한 라이센스 정보 저장 (상품 ID -> 라이센스 정보)
    const [acquiredLicenses, setAcquiredLicenses] = useState({});
    /**
   * 상품이 낙관적 업데이트로 획득된 상태인지 확인
   */ const getAcquiredLicenseInfo = useCallback((productId)=>{
        return acquiredLicenses[productId];
    }, [
        acquiredLicenses
    ]);
    /**
   * 샘플 보기 버튼 클릭 핸들러 생성
   */ const createSampleClickHandler = useCallback((item)=>{
        return ()=>openPreviewDialog({
                id: item.id.toString()
            });
    }, [
        openPreviewDialog
    ]);
    /**
   * 이용 수단(이용권/구독)으로 상품 획득 후 뷰어 열기 또는 다운로드
   * - 첫 사용 여부를 먼저 확인하여 환불 방어 다이얼로그 표시 여부 결정 (이용권 한정, 아래 주석 참고)
   * - 다이얼로그에서 "이용" 클릭 시 담기 API 호출
   * - 응답의 licenseIds[0]로 뷰어 열기 또는 바로 다운로드
   */ const acquireWithEntitlement = useCallback(async (item, purchaseOption, action)=>{
        const entitlement = getUsableEntitlement(item);
        if (!entitlement) {
            ;
            return;
        }
        // 다운로드 액션인 경우 인앱 브라우저 체크 선행 (이용권 소비 전에 차단)
        if (action === "download" && !checkDownloadBrowser()) {
            return;
        }
        try {
            // 1. 첫 사용 여부 확인 (구독은 확인하지 않고 항상 false — 판정 근거는 유틸 주석 참고)
            const isFirstUse = await checkEntitlementFirstUse(entitlement);
            // 2. 담기 + 후속 액션 실행 함수 (licenseId 반환)
            //    실패 안내는 이 함수 안에서 끝낸다 — 첫 사용 경로는 다이얼로그 콜백으로 넘어가
            //    바깥 try/catch 에 걸리지 않기 때문이다.
            const useEntitlementAndExecute = async ()=>{
                try {
                    var _response_licenseIds;
                    // 2-1. 담기 API 호출 (이용권/구독은 디스패처가 가른다)
                    const response = await requestProductUse({
                        productIds: [
                            item.id
                        ],
                        entitlement,
                        choicePurchaseOption: purchaseOption
                    });
                    // 2-2. 응답에서 licenseId 추출
                    const licenseId = (_response_licenseIds = response.licenseIds) === null || _response_licenseIds === void 0 ? void 0 : _response_licenseIds[0];
                    if (!licenseId) {
                        return undefined;
                    }
                    // 2-3. 낙관적 업데이트: 획득한 라이센스 정보 저장
                    setAcquiredLicenses((prev)=>({
                            ...prev,
                            [item.id]: {
                                licenseId,
                                licensePlan: purchaseOption
                            }
                        }));
                    // 2-4. N-PDF 체크 (뷰어/다운로드 공통)
                    if (item.hasMultiplePdfs && !shouldSkipNPdfDialog()) {
                        setNPdfTargetItem({
                            ...item,
                            licenseId
                        });
                        setNPdfActionType(action === "viewer" ? "view" : "download");
                        return licenseId;
                    }
                    if (action === "viewer") {
                        openViewerPage(licenseId);
                    } else {
                        setDownloadData({
                            id: licenseId,
                            title: item.title,
                            canEmbedLogo: true
                        });
                        setIsFirstUseForDownload(false);
                        setIsDownloadDialogOpen(true);
                    }
                    return licenseId;
                } catch (error) {
                    ;
                    openBlockedDialog(error, {
                        position,
                        productId: item.id
                    });
                    return undefined;
                }
            };
            // 3. 첫 사용이면 환불 방어 다이얼로그 표시 후 "이용" 클릭 시 담기, 아니면 바로 담기
            if (isFirstUse) {
                openLicenseUseDialog(async ()=>{
                    const licenseId = await useEntitlementAndExecute();
                    // mx-426: Popup Button Clicked (이용 버튼 - 담기 응답 후 발송)
                    if (licenseId) {
                        const popupTitle = "자료 이용 전 아래 사항을 꼭 확인해주세요.";
                        mixpanelEvent("Popup Button Clicked", {
                            "Position": position,
                            "Popup Type": "자료 이용 전 환불 규정 안내",
                            "Popup Title": popupTitle,
                            "Title": popupTitle,
                            "Body": popupTitle,
                            "Button Name": "확인",
                            "Button Url": "".concat(VIEWER_PAGE, "/").concat(licenseId)
                        });
                    }
                }, position);
            } else {
                await useEntitlementAndExecute();
            }
        } catch (error) {
            ;
        }
    }, [
        openLicenseUseDialog,
        checkDownloadBrowser,
        openBlockedDialog,
        position
    ]);
    /**
   * CTA 버튼 클릭 핸들러 생성
   *
   * 우선순위:
   * 1. 낙관적 업데이트로 획득한 라이센스가 있으면: 바로 뷰어 열기 (API 호출 안 함)
   * 2. licenseId가 있으면 (이미 자료 소유): 바로 뷰어 열기
   * 3. 이용 수단(이용권/구독) + purchaseOptions가 있으면: 획득 후 뷰어 열기
   * 4. freepass 타입: authorId 기반 이용권 페이지로 이동
   * 5. freeTrial 타입: 무료체험 다이얼로그
   * 6. trialOver/excluded: purchaseUrl로 이동
   */ const createCtaClickHandler = useCallback((item)=>{
        var _item_purchaseOptions, _item_freeTrial;
        const type = getProductType(item);
        // 1. 낙관적 업데이트로 획득한 라이센스가 있으면 바로 뷰어 열기 (use-as-membership 재호출 안 함)
        const acquiredInfo = acquiredLicenses[item.id];
        if (acquiredInfo) {
            return ()=>{
                if (item.hasMultiplePdfs && !shouldSkipNPdfDialog()) {
                    setNPdfTargetItem({
                        ...item,
                        licenseId: acquiredInfo.licenseId
                    });
                    setNPdfActionType("view");
                    return;
                }
                openViewerPage(acquiredInfo.licenseId);
            };
        }
        // 2. licenseId가 있으면 이미 자료를 소유한 케이스
        if (item.licensePlan === "view" || item.licensePlan === "download") {
            if (!item.licenseId) {
                return;
            }
            return async ()=>{
                // N-PDF 체크
                if (item.hasMultiplePdfs && !shouldSkipNPdfDialog()) {
                    setNPdfTargetItem(item);
                    setNPdfActionType("view");
                    return;
                }
                if (item.handoutType === "basic") {
                    let isFirstUse = false;
                    try {
                        const result = await checkFirstUse(item.licenseId);
                        isFirstUse = result.isFirstUse;
                    } catch (error) {
                        ;
                    }
                    if (isFirstUse) {
                        // 환불 확인 다이얼로그 → 확인 클릭은 유저 제스처이므로 팝업 차단 안 됨
                        openRefundConfirmDialog(()=>{
                            void logUsage({
                                licenseId: item.licenseId,
                                actionType: "view"
                            }).catch((error)=>{
                                ;
                            });
                            openViewerPage(item.licenseId);
                        });
                        return;
                    }
                    // await 이후라 팝업이 차단될 수 있음 → 차단 시 현재 탭에서 이동
                    const popup = getPopup();
                    if (popup) {
                        openViewerPage(item.licenseId, popup);
                    } else {
                        router.push("".concat(VIEWER_PAGE, "/").concat(item.licenseId));
                    }
                    return;
                }
                openViewerPage(item.licenseId);
            };
        }
        // 3. 이용 수단(이용권/구독) + purchaseOptions가 있으면 획득 후 뷰어 열기
        if (getUsableEntitlement(item) && ((_item_purchaseOptions = item.purchaseOptions) === null || _item_purchaseOptions === void 0 ? void 0 : _item_purchaseOptions.length)) {
            const purchaseOption = item.purchaseOptions.includes("download") ? "download" : "view";
            return ()=>acquireWithEntitlement(item, purchaseOption, "viewer");
        }
        // 4. freepass 타입: authorId 기반 이용권 페이지로 이동 (licenseId, 이용 수단 없는 경우)
        if (type === "freepass") {
            const passesUrl = getPassesUrlByAuthorId(item.authorId);
            return ()=>window.open(passesUrl, "_blank");
        }
        // 5. freeTrial 타입: 무료체험 다이얼로그
        if (type === "freeTrial" && ((_item_freeTrial = item.freeTrial) === null || _item_freeTrial === void 0 ? void 0 : _item_freeTrial.promotionId) && (item.freeTrial.eligible || item.freeTrial.reason === "not_logged_in")) {
            return ()=>openFreeTrialDialog(item.id, item.freeTrial.promotionId);
        }
        // 6. trialOver/excluded: purchaseUrl로 이동
        if ((type === "trialOver" || type === "excluded") && purchaseUrl) {
            return ()=>router.push(purchaseUrl);
        }
        return undefined;
    }, [
        acquireWithEntitlement,
        acquiredLicenses,
        openFreeTrialDialog,
        purchaseUrl,
        router,
        checkFirstUse,
        logUsage,
        openRefundConfirmDialog
    ]);
    /**
   * PDF 다운로드 버튼 클릭 핸들러 생성
   *
   * 우선순위:
   * 1. 낙관적 업데이트로 획득한 라이센스가 있고 download 플랜이면: 바로 다운로드 (API 호출 안 함)
   * 2. licenseId가 있고 licensePlan이 download면 (이미 자료 소유): 바로 다운로드
   * 3. 이용 수단(이용권/구독) + purchaseOptions에 download 포함: 획득 후 다운로드
   */ /**
   * 다운로드 다이얼로그 열기 헬퍼
   */ const openDownloadDialog = useCallback(function(licenseId, title) {
        let isFirstUse = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
        setDownloadData({
            id: licenseId,
            title,
            canEmbedLogo: true
        });
        setIsFirstUseForDownload(isFirstUse);
        setIsDownloadDialogOpen(true);
    }, []);
    /**
   * 다운로드 실행 (N-PDF 체크 포함)
   * - hasMultiplePdfs === true && 오늘 스킵 아님 → N-PDF 다이얼로그
   * - 그 외 → 기존 다운로드 플로우
   */ const executeDownload = useCallback(async (item, licenseId)=>{
        if (item.hasMultiplePdfs && !shouldSkipNPdfDialog()) {
            setNPdfTargetItem(item);
            setNPdfActionType("download");
            return;
        }
        if (item.handoutType === "basic") {
            try {
                const { isFirstUse  } = await checkFirstUse(licenseId);
                openDownloadDialog(licenseId, item.title, isFirstUse);
            } catch (error) {
                ;
                openDownloadDialog(licenseId, item.title);
            }
        } else {
            openDownloadDialog(licenseId, item.title);
        }
    }, [
        checkFirstUse,
        openDownloadDialog
    ]);
    /**
   * N-PDF 다이얼로그 "바로 다운로드/열람" 클릭 시
   */ const handleNPdfDirectAction = useCallback(async ()=>{
        var _acquiredLicenses_targetItem_id;
        if (!nPdfTargetItem) return;
        const targetItem = nPdfTargetItem;
        const actionType = nPdfActionType;
        setNPdfTargetItem(null);
        var _acquiredLicenses_targetItem_id_licenseId;
        const licenseId = (_acquiredLicenses_targetItem_id_licenseId = (_acquiredLicenses_targetItem_id = acquiredLicenses[targetItem.id]) === null || _acquiredLicenses_targetItem_id === void 0 ? void 0 : _acquiredLicenses_targetItem_id.licenseId) !== null && _acquiredLicenses_targetItem_id_licenseId !== void 0 ? _acquiredLicenses_targetItem_id_licenseId : targetItem.licenseId;
        if (!licenseId) return;
        // 열람 및 출력: 뷰어 열기
        if (actionType === "view") {
            openViewerPage(licenseId);
            return;
        }
        // 다운로드
        if (targetItem.handoutType === "basic") {
            try {
                const { isFirstUse  } = await checkFirstUse(licenseId);
                openDownloadDialog(licenseId, targetItem.title, isFirstUse);
            } catch (error) {
                ;
                openDownloadDialog(licenseId, targetItem.title);
            }
        } else {
            openDownloadDialog(licenseId, targetItem.title);
        }
    }, [
        nPdfTargetItem,
        nPdfActionType,
        acquiredLicenses,
        checkFirstUse,
        openDownloadDialog,
        openViewerPage
    ]);
    /**
   * N-PDF 다이얼로그 "함께 제공되는 자료 보러가기" 클릭 시
   */ const handleNPdfGoToDetail = useCallback(()=>{
        var _acquiredLicenses_nPdfTargetItem_id;
        if (!nPdfTargetItem) return;
        setNPdfTargetItem(null);
        var _acquiredLicenses_nPdfTargetItem_id_licenseId;
        const licenseId = (_acquiredLicenses_nPdfTargetItem_id_licenseId = (_acquiredLicenses_nPdfTargetItem_id = acquiredLicenses[nPdfTargetItem.id]) === null || _acquiredLicenses_nPdfTargetItem_id === void 0 ? void 0 : _acquiredLicenses_nPdfTargetItem_id.licenseId) !== null && _acquiredLicenses_nPdfTargetItem_id_licenseId !== void 0 ? _acquiredLicenses_nPdfTargetItem_id_licenseId : nPdfTargetItem.licenseId;
        if (licenseId) {
            router.push("/libraries/".concat(licenseId));
        }
    }, [
        nPdfTargetItem,
        acquiredLicenses,
        router
    ]);
    const createPdfDownloadHandler = useCallback((item)=>{
        const acquiredInfo = acquiredLicenses[item.id];
        const { available , hasLicense  } = getDownloadAvailability(item, acquiredInfo);
        if (!available) return undefined;
        if (hasLicense) {
            var _acquiredInfo_licenseId;
            const licenseId = (_acquiredInfo_licenseId = acquiredInfo === null || acquiredInfo === void 0 ? void 0 : acquiredInfo.licenseId) !== null && _acquiredInfo_licenseId !== void 0 ? _acquiredInfo_licenseId : item.licenseId;
            return ()=>executeDownload(item, licenseId);
        }
        // 이용 수단(이용권/구독)으로 다운로드 획득 필요
        return ()=>acquireWithEntitlement(item, "download", "download");
    }, [
        acquireWithEntitlement,
        acquiredLicenses,
        executeDownload
    ]);
    /**
   * 다운로드 다이얼로그 닫기
   */ const closeDownloadDialog = useCallback(()=>{
        setIsDownloadDialogOpen(false);
        setDownloadData(null);
        setIsFirstUseForDownload(false);
    }, []);
    /**
   * 다운로드 완료 핸들러
   */ const handleDownloadComplete = useCallback(async (success)=>{
        if (success && (downloadData === null || downloadData === void 0 ? void 0 : downloadData.id)) {
            try {
                await logUsage({
                    licenseId: downloadData.id,
                    actionType: "download"
                });
            } catch (error) {
                ;
            }
        }
        if (!success) {
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    }, [
        addSnackbar,
        downloadData,
        logUsage
    ]);
    var _acquiredLicenses_nPdfTargetItem_id_licenseId, _ref;
    return {
        createSampleClickHandler,
        createCtaClickHandler,
        createPdfDownloadHandler,
        isDownloadDialogOpen,
        downloadData,
        isFirstUseForDownload,
        closeDownloadDialog,
        handleDownloadComplete,
        acquiredLicenses,
        getAcquiredLicenseInfo,
        nPdfOpen: nPdfTargetItem !== null,
        closeNPdfDialog: ()=>setNPdfTargetItem(null),
        nPdfCount: nPdfTargetItem === null || nPdfTargetItem === void 0 ? void 0 : nPdfTargetItem.pdfCount,
        nPdfActionType,
        handleNPdfDirectAction,
        handleNPdfGoToDetail,
        nPdfDetailUrl: nPdfTargetItem ? "/libraries/".concat((_ref = (_acquiredLicenses_nPdfTargetItem_id_licenseId = (_acquiredLicenses_nPdfTargetItem_id = acquiredLicenses[nPdfTargetItem.id]) === null || _acquiredLicenses_nPdfTargetItem_id === void 0 ? void 0 : _acquiredLicenses_nPdfTargetItem_id.licenseId) !== null && _acquiredLicenses_nPdfTargetItem_id_licenseId !== void 0 ? _acquiredLicenses_nPdfTargetItem_id_licenseId : nPdfTargetItem.licenseId) !== null && _ref !== void 0 ? _ref : "") : ""
    };
};
